import type { SxProps, Theme } from "@mui/material";
import { Box, Chip, IconButton, List, ListItem, Typography } from "@mui/material";
import { useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

import { GetActivitiesQuery } from "@/graphql/graphql";

import type { ActivitiesQueryState } from "../ActivitiesTable";
import {
  categoryPillSx,
  neutralPillFilterMatchSx,
  neutralPillSx,
  pillContainerSx,
  planRemoveButtonInSelectedListSx,
  planToggleGlyphSx,
} from "../ActivitiesTable";
import {
  formatEnumLabel,
  MAX_CALL_PLAN_ACTIVITIES,
  planTableToggleAriaLabel,
} from "../CreateCallPlanModal.utils";
import {
  selectedActivitiesEmptyHintSx,
  selectedActivitiesListHeaderCellSx,
  selectedActivitiesListHeaderGridSx,
  selectedActivitiesListHeaderRowSx,
  selectedActivitiesListItemDragCloneSx,
  selectedActivitiesListItemSx,
  selectedActivitiesToggleColumnSpacerSx,
  selectedActivityCategoryCellSx,
  selectedActivityPillColumnSx,
  selectedActivityRowGridDraggingSx,
  selectedActivityRowGridSx,
  selectedActivityRowTextSx,
} from "./SelectedActivitiesList.styles";

const SELECTED_ACTIVITIES_DROPPABLE_ID = "call-plan-selected-activities";

type PlanActivityRow = GetActivitiesQuery["activities"]["activities"][number];

type SelectedActivitiesListProps = {
  activities: PlanActivityRow[];
  onReorder: (activities: PlanActivityRow[]) => void;
  onRemove: (activityId: number) => void;
  queryState: ActivitiesQueryState;
  typeKeysSelected: Record<string, boolean>;
};

const ActivityRowFields = ({
  activity,
  queryState,
  typeKeysSelected,
}: {
  activity: PlanActivityRow;
  queryState: ActivitiesQueryState;
  typeKeysSelected: Record<string, boolean>;
}) => (
  <>
    <Typography sx={selectedActivityRowTextSx} noWrap title={activity.title} component="div">
      {activity.title}
    </Typography>
    <Box sx={selectedActivityCategoryCellSx}>
      <Chip size="small" label={formatEnumLabel(activity.category)} sx={categoryPillSx} />
    </Box>
    <Box sx={selectedActivityPillColumnSx}>
      {activity.activityTypes.length ? (
        <Box sx={pillContainerSx}>
          {activity.activityTypes.map((type) => (
            <Chip
              key={type.activityTypeKey}
              size="small"
              label={type.activityTypeLabel}
              sx={
                {
                  ...neutralPillSx,
                  ...(queryState.typeKeys?.length && typeKeysSelected[type.activityTypeKey]
                    ? neutralPillFilterMatchSx
                    : {}),
                } as SxProps<Theme>
              }
            />
          ))}
        </Box>
      ) : (
        <Typography sx={selectedActivityRowTextSx} component="span">
          -
        </Typography>
      )}
    </Box>
    <Typography sx={selectedActivityRowTextSx} component="div">
      {formatEnumLabel(activity.difficulty)}
    </Typography>
  </>
);

const SelectedActivitiesList: React.FC<SelectedActivitiesListProps> = ({
  activities,
  onReorder,
  onRemove,
  queryState,
  typeKeysSelected,
}) => {
  const [dragPreview, setDragPreview] = useState<PlanActivityRow | null>(activities[0] ?? null);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    // reorder the activities array
    const next = [...activities];
    const [removed] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, removed);
    onReorder(next);
  };

  if (activities.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={selectedActivitiesEmptyHintSx}>
        {`Add up to ${MAX_CALL_PLAN_ACTIVITIES} activities using the + button in the table.`}
      </Typography>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box sx={selectedActivitiesListHeaderRowSx}>
        <Box sx={selectedActivitiesToggleColumnSpacerSx} aria-hidden />
        <Box sx={selectedActivitiesListHeaderGridSx}>
          <Typography sx={selectedActivitiesListHeaderCellSx} component="div">
            Title
          </Typography>
          <Typography sx={selectedActivitiesListHeaderCellSx} component="div">
            Category
          </Typography>
          <Typography sx={selectedActivitiesListHeaderCellSx} component="div">
            Activity Types
          </Typography>
          <Typography sx={selectedActivitiesListHeaderCellSx} component="div">
            Difficulty
          </Typography>
        </Box>
      </Box>

      <Droppable
        droppableId={SELECTED_ACTIVITIES_DROPPABLE_ID}
        renderClone={(provided) => (
          <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
            <ListItem sx={selectedActivitiesListItemDragCloneSx}>
              <Box sx={selectedActivitiesToggleColumnSpacerSx} />
              <Box sx={selectedActivityRowGridDraggingSx}>
                {dragPreview ? (
                  <ActivityRowFields
                    activity={dragPreview}
                    queryState={queryState}
                    typeKeysSelected={typeKeysSelected}
                  />
                ) : null}
              </Box>
            </ListItem>
          </div>
        )}
      >
        {(provided) => (
          <List ref={provided.innerRef} {...provided.droppableProps}>
            {activities.map((activity, index) => (
              <Draggable key={activity.id} draggableId={String(activity.id)} index={index}>
                {(dragProvided) => (
                  <ListItem
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    divider
                    sx={selectedActivitiesListItemSx}
                  >
                    <IconButton
                      size="small"
                      aria-label={planTableToggleAriaLabel(activity.title, "remove")}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(activity.id);
                      }}
                      sx={planRemoveButtonInSelectedListSx}
                    >
                      <Typography component="span" sx={planToggleGlyphSx}>
                        -
                      </Typography>
                    </IconButton>
                    <Box
                      {...dragProvided.dragHandleProps}
                      onMouseDown={() => setDragPreview(activity)}
                      sx={selectedActivityRowGridSx}
                    >
                      <ActivityRowFields
                        activity={activity}
                        queryState={queryState}
                        typeKeysSelected={typeKeysSelected}
                      />
                    </Box>
                  </ListItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default SelectedActivitiesList;
